/**
 * Calendar Utilities for Adding Appointments to Calendar
 * Supports Google Calendar, Apple Calendar, Outlook, and iCal downloads
 */

/**
 * Format date and time for calendar events
 */
const formatDateTime = (date, time) => {
  // Combine date and time into ISO format
  const dateTimeString = `${date}T${time}`;
  const dateObj = new Date(dateTimeString);
  
  // Format for iCal (YYYYMMDDTHHmmss)
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}00`;
};

/**
 * Calculate end time (appointment duration defaults to 30 minutes)
 */
const calculateEndTime = (date, time, durationMinutes = 30) => {
  const dateTimeString = `${date}T${time}`;
  const dateObj = new Date(dateTimeString);
  dateObj.setMinutes(dateObj.getMinutes() + durationMinutes);
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}00`;
};

/**
 * Generate iCal file content
 */
const generateICS = (appointment) => {
  const startTime = formatDateTime(appointment.appointment_date, appointment.appointment_time);
  const endTime = calculateEndTime(appointment.appointment_date, appointment.appointment_time, 30);
  
  const title = appointment.service_name 
    ? `${appointment.service_name} Appointment`
    : 'Medical Appointment';
  
  const location = appointment.clinic_name || appointment.provider_name || 'EnayaHub Healthcare';
  
  let description = `Appointment at ${location}`;
  if (appointment.confirmation_number) {
    description += `\\nConfirmation: ${appointment.confirmation_number}`;
  }
  if (appointment.service_name) {
    description += `\\nService: ${appointment.service_name}`;
  }
  if (appointment.staff_name) {
    description += `\\nStaff: ${appointment.staff_name}`;
  }
  if (appointment.symptoms) {
    description += `\\nSymptoms: ${appointment.symptoms}`;
  }
  if (appointment.notes) {
    description += `\\nNotes: ${appointment.notes}`;
  }
  
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//EnayaHub//Healthcare Appointments//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${startTime}`,
    `DTEND:${endTime}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    `STATUS:CONFIRMED`,
    'BEGIN:VALARM',
    'TRIGGER:-PT24H',
    'ACTION:DISPLAY',
    'DESCRIPTION:Appointment Reminder',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  return icsContent;
};

/**
 * Download ICS file
 */
export const downloadICS = (appointment) => {
  const icsContent = generateICS(appointment);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `appointment-${appointment.confirmation_number || 'enayahub'}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate Google Calendar URL
 */
export const getGoogleCalendarUrl = (appointment) => {
  const startDateTime = new Date(`${appointment.appointment_date}T${appointment.appointment_time}`);
  const endDateTime = new Date(startDateTime);
  endDateTime.setMinutes(endDateTime.getMinutes() + 30);
  
  // Format: YYYYMMDDTHHmmss
  const formatForGoogle = (date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, -1);
  };
  
  const title = appointment.service_name 
    ? `${appointment.service_name} Appointment`
    : 'Medical Appointment';
  
  const location = appointment.clinic_name || appointment.provider_name || 'EnayaHub Healthcare';
  
  let details = `Appointment at ${location}`;
  if (appointment.confirmation_number) {
    details += `\nConfirmation: ${appointment.confirmation_number}`;
  }
  if (appointment.service_name) {
    details += `\nService: ${appointment.service_name}`;
  }
  if (appointment.staff_name) {
    details += `\nStaff: ${appointment.staff_name}`;
  }
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatForGoogle(startDateTime)}/${formatForGoogle(endDateTime)}`,
    details: details,
    location: location,
    trp: 'false' // Don't show "Add guests" section
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL
 */
export const getOutlookCalendarUrl = (appointment) => {
  const startDateTime = new Date(`${appointment.appointment_date}T${appointment.appointment_time}`);
  const endDateTime = new Date(startDateTime);
  endDateTime.setMinutes(endDateTime.getMinutes() + 30);
  
  const title = appointment.service_name 
    ? `${appointment.service_name} Appointment`
    : 'Medical Appointment';
  
  const location = appointment.clinic_name || appointment.provider_name || 'EnayaHub Healthcare';
  
  let body = `Appointment at ${location}`;
  if (appointment.confirmation_number) {
    body += `\nConfirmation: ${appointment.confirmation_number}`;
  }
  if (appointment.service_name) {
    body += `\nService: ${appointment.service_name}`;
  }
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    startdt: startDateTime.toISOString(),
    enddt: endDateTime.toISOString(),
    body: body,
    location: location
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Yahoo Calendar URL
 */
export const getYahooCalendarUrl = (appointment) => {
  const startDateTime = new Date(`${appointment.appointment_date}T${appointment.appointment_time}`);
  const endDateTime = new Date(startDateTime);
  endDateTime.setMinutes(endDateTime.getMinutes() + 30);
  
  const title = appointment.service_name 
    ? `${appointment.service_name} Appointment`
    : 'Medical Appointment';
  
  const location = appointment.clinic_name || appointment.provider_name || 'EnayaHub Healthcare';
  
  let desc = `Appointment at ${location}`;
  if (appointment.confirmation_number) {
    desc += `\nConfirmation: ${appointment.confirmation_number}`;
  }
  
  const params = new URLSearchParams({
    v: '60',
    title: title,
    st: startDateTime.toISOString().replace(/-|:|\.\d+/g, '').slice(0, -1),
    dur: '0030', // 30 minutes
    desc: desc,
    in_loc: location
  });
  
  return `https://calendar.yahoo.com/?${params.toString()}`;
};

/**
 * Check if device is iOS/Mac for appropriate calendar options
 */
export const isAppleDevice = () => {
  return /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
};

/**
 * Check if device is Android
 */
export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};
